import React, { useCallback, useMemo } from 'react';
import { useFormikContext, FieldArray } from 'formik';
import { useGridApiRef } from '@mui/x-data-grid-premium';
import { Box, Paper, Typography } from '@mui/material';

import { dataGridPremiumColumns } from '../../../utils/helper';
import { DataGridIdentifiers } from '../../Interface';
import { CheckboxUI, DatagridPremiumUi, FormErrorUI } from '../../Interface';
import { AlertTypeEnum } from '../../../config/alert';

const AlertFormUsersTable = (props) => {
  const { users = [], roles = [] } = props;
  const apiRef = useGridApiRef();

  const { values, errors } = useFormikContext();
  const internalRecipients = useMemo(() => values.alertRecipient ?? [], [values.alertRecipient]);
  const emailRecipients = useMemo(() => values.alertEmailRecipient ?? [], [values.alertEmailRecipient]);
  const showInternalRecipients = values.type === AlertTypeEnum.ALERT;
  const recipientRows = useMemo(
    () => [
      ...users.map((user) => ({
        ...user,
        recipientKey: `user-${user.userId}`,
        recipientType: 'USER',
        recipientId: user.userId,
        recipientName: user.nickName,
        recipientDetails: user.userName,
      })),
      ...roles.map((role) => ({
        ...role,
        recipientKey: `group-${role.roleId}`,
        recipientType: 'GROUP',
        recipientId: role.roleId,
        recipientName: role.roleName,
        recipientDetails: 'Szerepkör',
      })),
    ],
    [roles, users]
  );

  const findRecipient = useCallback(
    (recipients, row) =>
      recipients.find((recipient) =>
        row.recipientType === 'USER'
          ? recipient.userId === row.recipientId
          : recipient.groupId === row.recipientId
      ),
    []
  );

  const columns = useMemo(
    () => (internalRemove, internalPush, emailRemove, emailPush) =>
      dataGridPremiumColumns([
        {
          field: 'recipientName',
          headerName: 'Címzett',
          type: 'string',
        },
        {
          field: 'recipientDetails',
          headerName: 'Típus / felhasználónév',
          type: 'string',
        },
        ...(showInternalRecipients
          ? [
              {
                field: 'quantWarn',
                headerName: 'Quant',
                type: 'boolean',
                sortable: false,
                valueGetter: (_value, row) => Boolean(findRecipient(internalRecipients, row)),
                renderCell: (data) => (
                  <CheckboxUI
                    name={`alertRecipient_${data.row.recipientKey}`}
                    checked={Boolean(findRecipient(internalRecipients, data.row))}
                    variant="standard"
                    size="small"
                    sx={{
                      p: 0.25,
                      '& .MuiSvgIcon-root': {
                        fontSize: 18,
                      },
                    }}
                    onChange={(event) => {
                      const recipient = findRecipient(internalRecipients, data.row);
                      if (event.target.checked && !recipient) {
                        internalPush(
                          data.row.recipientType === 'USER'
                            ? { userId: data.row.recipientId, groupId: null }
                            : { userId: null, groupId: data.row.recipientId }
                        );
                      } else if (!event.target.checked && recipient) {
                        internalRemove(internalRecipients.findIndex((item) => item === recipient));
                      }
                    }}
                  />
                ),
              },
            ]
          : []),
        {
          field: 'emailWarn',
          headerName: 'E-mail',
          type: 'boolean',
          sortable: false,
          valueGetter: (_value, row) => Boolean(findRecipient(emailRecipients, row)),
          renderCell: (data) => (
            <CheckboxUI
              name={`alertEmailRecipient_${data.row.recipientKey}`}
              checked={Boolean(findRecipient(emailRecipients, data.row))}
              disabled={
                data.row.recipientType === 'USER' &&
                !data.row.email &&
                !findRecipient(emailRecipients, data.row)
              }
              variant="standard"
              size="small"
              sx={{
                p: 0.25,
                '& .MuiSvgIcon-root': {
                  fontSize: 18,
                },
              }}
              onChange={(event) => {
                const recipient = findRecipient(emailRecipients, data.row);
                if (event.target.checked && !recipient) {
                  emailPush(
                    data.row.recipientType === 'USER'
                      ? { userId: data.row.recipientId, groupId: null }
                      : { userId: null, groupId: data.row.recipientId }
                  );
                } else if (!event.target.checked && recipient) {
                  emailRemove(emailRecipients.findIndex((item) => item === recipient));
                }
              }}
            />
          ),
        },
      ]),
    [emailRecipients, findRecipient, internalRecipients, showInternalRecipients]
  );

  return (
    <Box sx={{ overflowX: 'auto' }} pt={1}>
      <Paper
        elevation={1}
        sx={{
          overflow: 'hidden',
          borderRadius: { xs: 1, sm: 2 },
        }}
      >
        <Typography variant="body2" color="text.secondary" fontWeight={600} fontSize={'1rem'} my={1}>
          Címzettek
        </Typography>
        {errors.alertRecipient && showInternalRecipients ? (
          <FormErrorUI
            message={
              typeof errors.alertRecipient === 'string'
                ? errors.alertRecipient
                : 'Kérjük, ellenőrizze a belső értesítés címzettjeit.'
            }
          />
        ) : null}
        {errors.alertEmailRecipient ? (
          <FormErrorUI
            message={
              typeof errors.alertEmailRecipient === 'string'
                ? errors.alertEmailRecipient
                : 'Kérjük, ellenőrizze az e-mail címzettjeit.'
            }
          />
        ) : null}
        <FieldArray name="alertRecipient">
          {({ remove: internalRemove, push: internalPush }) => (
            <FieldArray name="alertEmailRecipient">
              {({ remove: emailRemove, push: emailPush }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridIdentifier={DataGridIdentifiers.AlertRecipient}
                  height={660}
                  columns={columns(internalRemove, internalPush, emailRemove, emailPush)}
                  rows={recipientRows}
                  rowCount={recipientRows.length}
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  getRowHeight={() => 'auto'}
                  getRowId={(row) => row.recipientKey}
                />
              )}
            </FieldArray>
          )}
        </FieldArray>
      </Paper>
    </Box>
  );
};

export default AlertFormUsersTable;
